// Lib
import { isEqual } from 'lodash/fp';
import { sum } from 'lodash';

// Actions
import { createAndSelectElement, updateElement } from '../actions/elementActions';

// Selectors
import { getCurrentCellSelections } from './tableSelector';
import { userLanguagePreferenceSelector } from '../../user/currentUserSelector';
import { getElements } from '../selectors/elementsSelector';
import { getIsFeatureEnabledForCurrentUser } from '../feature/elementFeatureSelector';

// Utils
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import {
    deselectCellSelectionSubset,
    isSelectionSubsetOf,
    reorderCellSelection,
} from './utils/tableCellSelectionUtils';
import { asObject } from '../../../common/utils/immutableHelper';
import { convertTableElementChangesToReduxFormat } from './utils/tableDataUtils';
import { TableDataVersion } from '../../../common/table/TableTypes';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getTableContentVersion } from '../../../common/elements/utils/elementPropertyUtils';

// Constants
import * as SELECTION_ACTION_TYPES from '../../../common/elements/selectionConstants';
import { ELEMENT_UPDATE_TYPE } from '../../../common/elements/elementConstants';
import { ElementType } from '../../../common/elements/elementTypes';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';

export const createTable = (args) => (dispatch) => {
    const { location, content, currentBoardId, transactionId = getNewTransactionId(), creationSource } = args;

    return dispatch(
        createAndSelectElement({
            elementType: ElementType.TABLE_TYPE,
            location,
            content,
            currentBoardId,
            transactionId,
            creationSource,
        }),
    );
};

/**
 * Update the meta information for the selection, like cell selection for tables
 */
export const updateCellSelectionMeta =
    (id, cellSelection, selectionLayerLevel = 0, transactionId) =>
    (dispatch, getState) => {
        const state = getState();

        if (!cellSelection) {
            return dispatch({
                type: SELECTION_ACTION_TYPES.TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                id,
                cellSelections: null,
            });
        }

        const prevCellSelections = asObject(getCurrentCellSelections(state, { elementId: id })) || [];
        let newCellSelections = [];

        if (selectionLayerLevel === 0) {
            newCellSelections[selectionLayerLevel] = cellSelection;
        } else {
            const orderedCellSelection = reorderCellSelection(cellSelection);

            let shouldAddToCellSelections = true;
            prevCellSelections.forEach((prevCellSelection) => {
                const orderedPrevCellSelection = reorderCellSelection(prevCellSelection);

                if (isEqual(orderedCellSelection, orderedPrevCellSelection)) {
                    shouldAddToCellSelections = false;
                    return;
                }

                if (isSelectionSubsetOf(orderedCellSelection, orderedPrevCellSelection)) {
                    newCellSelections.push(
                        ...deselectCellSelectionSubset(orderedPrevCellSelection, orderedCellSelection),
                    );
                    shouldAddToCellSelections = false;
                    return;
                }

                newCellSelections.push(prevCellSelection);
            });

            if (shouldAddToCellSelections) {
                newCellSelections[selectionLayerLevel] = cellSelection;
            }
        }

        return dispatch({
            type: SELECTION_ACTION_TYPES.TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
            id,
            cellSelections: newCellSelections,
            transactionId,
        });
    };

/**
 * Gets the table content version to store on the element.
 *
 * If the table doesn't already specify a version & the user isn't on the V2 architecture, then don't
 * store anything.
 *
 * Otherwise, store the version that's already on the table.
 * If the table doesn't have a version already then store "V2".
 */
// TODO-TABLES - This is currently handling *both* the legacy & V2 element update flows.
//  After the architecture restructure we can hopefully isolate each flow
const getTableUpdateTableContentVersion = (isTableDataV2Enabled, currentElement) => {
    const currentTableContentVersion = getTableContentVersion(currentElement);

    // We should only store a version if the user has the V2 table architecture enabled,
    // or if the table already has a version stored
    const shouldAddContentVersion = isTableDataV2Enabled || !!currentTableContentVersion;

    if (!shouldAddContentVersion) return undefined;

    // Maintain the version already on the table
    if (currentTableContentVersion) return currentTableContentVersion;

    // The user must be on the V2 architecture, so use the V2 table
    return TableDataVersion.V2;
};

export const updateTableElement =
    ({ id, changes, batchUndoActions, batchRedoActions, transactionId, silent }) =>
    (dispatch, getState) => {
        const state = getState();
        const locale = userLanguagePreferenceSelector(state);

        // Defensively protect against adding the "version = V2" property if the user isn't V2 enabled
        const isTableDataV2Enabled = getIsFeatureEnabledForCurrentUser(ExperimentId.tableArchV2)(state);

        const elements = getElements(state);
        const currentElement = getElement(elements, id);

        // TODO-TABLES - This is currently handling *both* the legacy & V2 element update flows.
        //  After the architecture restructure we can hopefully isolate each flow
        const tableContentVersion = getTableUpdateTableContentVersion(isTableDataV2Enabled, currentElement);

        const changesWithTableDataVersion =
            tableContentVersion && changes?.tableContent
                ? {
                      ...changes,
                      tableContent: {
                          ...changes.tableContent,
                          version: tableContentVersion,
                      },
                  }
                : changes;
        const changesToDispatch = convertTableElementChangesToReduxFormat(changesWithTableDataVersion, locale);

        // if there is a change to the col widths, update the table width as well
        if (changesToDispatch?.tableContent?.colWidthsGU && !changesToDispatch.width) {
            changesToDispatch.width = Math.round(sum(changesToDispatch.tableContent.colWidthsGU));
        }
        dispatch(
            updateElement({
                id,
                updateType: ELEMENT_UPDATE_TYPE.TABLE,
                changes: changesToDispatch,
                batchUndoActions,
                batchRedoActions,
                transactionId,
                silent,
            }),
        );
    };
